"use client";
import React from "react";
import { Modal } from "antd";
import { Clock, Star } from "lucide-react";
import Image from "components/common/Image";
import { formatPrice } from "utils/currencyFormatter";
import { get } from "utils/lodash";
import { offerDiscountPct } from "services/campaignPackage/formatter";
import { useT } from "providers/I18nProvider";

/**
 * Chi tiết 1 dịch vụ trong Bảng giá (trang shop mobile — mockup v2 chốt 05/10).
 * antd Modal + rootClassName="lm-sheet" → bottom sheet trên mobile, modal giữa trên desktop.
 *
 * Nội dung: nhóm · tên · giá · thời lượng · số bước · shop · ưu đãi áp dụng cho
 * dịch vụ này (campaignPackage.service.id) · quy trình (parse Lexical "Bước N: …")
 * · dịch vụ cùng nhóm · nút "Đặt dịch vụ này" (mở store).
 * CMS KHÔNG có field "Phù hợp với" → không hiện (mockup chỉ minh hoạ).
 */

export interface SheetService {
  id: string;
  name: string;
  menuPrice?: number;
  duration?: number;
  isHighlight?: boolean;
  group: string;
  description?: any;
}

/** Lexical JSON → mảng đoạn văn bản (paragraph/heading/listitem) */
const lexicalBlocks = (desc: any): string[] => {
  const root = get(desc, "root");
  if (!root) return typeof desc === "string" && desc.trim() ? [desc.trim()] : [];
  const out: string[] = [];
  const textOf = (n: any): string =>
    n?.type === "linebreak"
      ? "\n"
      : typeof n?.text === "string"
        ? n.text
        : (n?.children || []).map(textOf).join("");
  const walk = (n: any) => {
    if (!n) return;
    if (["paragraph", "heading", "listitem", "quote"].includes(n.type)) {
      textOf(n)
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((s) => out.push(s));
      return;
    }
    (n.children || []).forEach(walk);
  };
  walk(root);
  return out;
};

const STEP_RE = /^(?:bước|step|b)\s*(\d+)\s*[:.)\-–]\s*(.+)$/i;

/** Tách mô tả thành các bước (nếu có dạng "Bước 1: …") + phần mô tả thường */
export const parseServiceDescription = (desc: any) => {
  const blocks = lexicalBlocks(desc);
  const steps: string[] = [];
  const intro: string[] = [];
  for (const b of blocks) {
    const m = STEP_RE.exec(b);
    if (m) steps.push(m[2].trim());
    else if (steps.length) steps[steps.length - 1] += ` ${b}`;
    else intro.push(b);
  }
  return { steps, intro };
};

interface Props {
  open: boolean;
  service?: SheetService | null;
  siblings: SheetService[];
  shop?: any;
  offers: any[];
  onClose: () => void;
  onSelect: (s: SheetService) => void;
  /** Bấm "Đặt dịch vụ này" → cha đóng sheet + mở BookingQRModal (báo tính năng trên app) */
  onBook: () => void;
}

const ServiceDetailSheet: React.FC<Props> = ({
  open,
  service,
  siblings,
  shop,
  offers,
  onClose,
  onSelect,
  onBook,
}) => {
  const t = useT();
  const bodyRef = React.useRef<HTMLDivElement>(null);

  // Đổi sang dịch vụ khác cùng nhóm → cuộn lại đầu sheet
  React.useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [service?.id]);

  if (!service) return null;
  const { steps, intro } = parseServiceDescription(service.description);
  // Một dịch vụ có thể nằm trong nhiều ưu đãi → lấy ưu đãi có giá sau giảm thấp nhất (tốt nhất cho khách)
  const offer = offers
    .filter((p) => get(p, "service.id") === service.id)
    .reduce<(typeof offers)[number] | undefined>((best, p) => {
      if (!best) return p;
      const price = Number(get(p, "displayDiscountPrice")) || Infinity;
      const bestPrice = Number(get(best, "displayDiscountPrice")) || Infinity;
      return price < bestPrice ? p : best;
    }, undefined);
  const offerPrice = Number(get(offer, "displayDiscountPrice")) || 0;
  const pct = offer ? offerDiscountPct(offer) : 0;
  const others = siblings.filter((s) => s.id !== service.id).slice(0, 8);
  const rating = Number(get(shop, "rating")) || 0;

  return (
    <Modal
      rootClassName="lm-sheet"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={560}
      destroyOnHidden
      styles={{ mask: { backgroundColor: "rgba(0,0,0,0.55)" } }}
    >
      <div ref={bodyRef} className="max-h-[70vh] overflow-y-auto -mx-1 px-1">
        <p className="!mb-1 text-xs font-bold uppercase tracking-wide text-primary">
          {service.group}
        </p>
        <h4 className="!mb-2 pr-8 text-[19px] font-extrabold leading-snug text-black01">
          {service.name}
        </h4>
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
          <span className="text-[18px] font-extrabold text-primary">
            {service.menuPrice ? formatPrice(service.menuPrice) : t("shopServices.contactPrice")}
          </span>
          {service.duration ? (
            <span className="inline-flex items-center gap-1">
              <Clock size={14} />
              {t("shopServices.minutes", { count: service.duration })}
            </span>
          ) : null}
          {steps.length ? <span>{t("shopServices.steps", { count: steps.length })}</span> : null}
        </div>

        {shop ? (
          <div className="mb-4 flex items-center gap-3 rounded-xl bg-gray-50 p-2.5">
            {get(shop, "avatar.url") ? (
              <Image
                wrapClassName="h-10 w-10 shrink-0 overflow-hidden rounded-lg"
                src={get(shop, "avatar.url")}
                alt={get(shop, "name")}
              />
            ) : null}
            <div className="min-w-0 text-[13px]">
              <b className="block truncate text-black01">{get(shop, "name")}</b>
              <span className="block truncate text-gray-500">
                {get(shop, "displayedAddress")}
                {rating ? (
                  <>
                    {" · "}
                    <Star size={11} className="-mt-0.5 inline fill-yellow-400 text-yellow-400" />{" "}
                    {rating.toFixed(1)}
                  </>
                ) : null}
              </span>
            </div>
          </div>
        ) : null}

        {offer && offerPrice ? (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50/60 p-3 text-[13px]">
            <div>
              {pct ? <b className="text-red-500">-{pct}% </b> : null}
              {service.menuPrice ? (
                <span className="text-gray-400 line-through">{formatPrice(service.menuPrice)}</span>
              ) : null}{" "}
              → <b className="text-black01">{formatPrice(offerPrice)}</b>
              <br />
              <span className="text-gray-500">{t("shopServices.offerNote")}</span>
            </div>
            <span className="shrink-0 rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white">
              {t("shopServices.hasOffer")}
            </span>
          </div>
        ) : null}

        {intro.length ? (
          <div className="mb-4 text-[14px] leading-relaxed text-gray-700">
            {intro.map((p, i) => (
              <p key={i} className="!mb-1.5">{p}</p>
            ))}
          </div>
        ) : null}

        {steps.length ? (
          <>
            <h5 className="!mb-2 text-[15px] font-extrabold text-black01">
              {t("shopServices.process")}
            </h5>
            <ol className="mb-4 flex flex-col gap-2.5">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-[14px] leading-snug text-gray-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
          </>
        ) : null}

        {others.length ? (
          <>
            <h5 className="!mb-2 text-[15px] font-extrabold text-black01">
              {t("shopServices.sameGroup")}
            </h5>
            <div className="-mx-1 mb-2 flex gap-2.5 overflow-x-auto px-1 pb-1 scrollbar-hide">
              {others.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onSelect(s)}
                  className="w-[160px] shrink-0 rounded-xl border border-gray-200 p-2.5 text-left"
                >
                  <span className="line-clamp-2 block text-[13px] font-semibold leading-snug text-black01">
                    {s.name}
                  </span>
                  <span className="mt-1 block text-xs text-gray-500">
                    {s.duration ? `${t("shopServices.minutes", { count: s.duration })} · ` : ""}
                    <b className="text-primary">
                      {s.menuPrice ? formatPrice(s.menuPrice) : t("shopServices.contactPrice")}
                    </b>
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-3 border-t border-gray-100 pt-3">
        <div className="min-w-0">
          <small className="block text-xs text-gray-500">{t("shopServices.priceLabel")}</small>
          <b className="text-[17px] text-black01">
            {offerPrice || service.menuPrice
              ? formatPrice(offerPrice || service.menuPrice || 0)
              : t("shopServices.contactPrice")}
          </b>
        </div>
        <button
          type="button"
          onClick={onBook}
          className="ml-auto flex h-11 flex-1 max-w-[220px] items-center justify-center rounded-full text-[15px] font-semibold"
          style={{ backgroundColor: "var(--primary)", color: "#fff" }}
        >
          {t("shopServices.bookThis")}
        </button>
      </div>
    </Modal>
  );
};

export default ServiceDetailSheet;
